import type { LoaderFunction } from 'react-router';
import type { CompanyDetails } from '../../../shared/services/companyDetailsService/types';

import type { RouteProps } from '../../../shared/RouteProps';

import type { CultureModel } from './cultureModel';

const mapCultureModel = (
  companyProfile: CompanyDetails['companyProfile'],
): CultureModel => ({
  perksAndBenefits: companyProfile.data.perksAndBenefits
    ? companyProfile.data.perksAndBenefits.map((pb) => ({
        title: pb?.title ?? '',
        description: pb?.description,
      }))
    : undefined,
  cultureAndValues: companyProfile.data.cultureAndValues?.title
    ? {
        title: companyProfile.data.cultureAndValues.title,
        description: companyProfile.data.cultureAndValues?.description ?? [],
        imageUrl: companyProfile.data.cultureAndValues.imageUrl,
        values:
          companyProfile.data.cultureAndValues.values?.map((value) => ({
            title: value?.title ?? '',
            description: value?.description ?? '',
          })) ?? [],
      }
    : undefined,
  customStatement: companyProfile.data.customStatement?.title
    ? {
        title: companyProfile.data.customStatement.title,
        description:
          (companyProfile.data.customStatement?.description as string[]) ?? [],
      }
    : undefined,
  gallery: {
    name: 'gallery',
    photos:
      companyProfile.data.gallery?.map((photo) => ({
        link: photo?.linkUrl ?? '',
        date: photo?.date ?? undefined,
        title: photo?.title ?? undefined,
      })) ?? [],
  },
  companyName: companyProfile.data.name ?? '',
});

const extractCompanyIdFromSlug = (input?: string): string | undefined => {
  if (!input) {
    return undefined;
  }
  const parts = input.split('-');
  return parts[parts.length - 1];
};

export const cultureLoader =
  ({ services }: RouteProps): LoaderFunction =>
  async ({ params, request }): Promise<CultureModel> => {
    const companyId = extractCompanyIdFromSlug(params.companySlug);

    if (companyId === undefined) {
      throw new Error('Could not find companyId from params', params);
    }

    const url = new URL(request.url);
    const isDraft = url.searchParams.get('draft') === 'true';

    const companyProfile =
      await services.companyDetailsService.getCompanyDetails(
        companyId,
        Boolean(isDraft),
      );

    return mapCultureModel(companyProfile.companyProfile);
  };
